import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {TileId, TileIdMap} from '../../../shared/models/tile';
import {SortTableSortDirection} from '../../../shared/models/sort-table/types';
import {Option, OptionGroupMultiSelect} from '../../../shared/models/option';
import {DataPoint} from "../../../shared/models/structure";
import {TileIconComponent} from '../tile-icon/tile-icon.component';
import {PillComponent} from '../pill/pill.component';
import {PopoverSelectTilesComponent} from '../popover-multi-select/object-types/tiles/popover-select-tiles.component';

@Component({
  selector: 'motn-tile-order-sorter',
  templateUrl: './tile-order-sorter.component.html',
  styleUrls: ['./tile-order-sorter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TileIconComponent,
    PillComponent,
    PopoverSelectTilesComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
  ],
})
export class TileOrderSorterComponent {
  @Input() tiles: TileIdMap;
  @Input() tileOrder: TileId[];
  @Input() dataPoints: DataPoint[];
  @Input() selectableTilesOptionGroup: OptionGroupMultiSelect;
  @Input() isDataPointsViewMode = false;

  @Input() listIdForCdk: string = null;
  @Input() connectedListIdsForCdk: string[] = null;

  menuOpen: boolean = false;
  private sortDirection: SortTableSortDirection;
  private manualOrder: TileId[] = [];

  @Output() clickedX$: EventEmitter<TileId> = new EventEmitter<TileId>();
  @Output() clickedOption$: EventEmitter<Option> = new EventEmitter<Option>();
  @Output() draggedAndDropped$: EventEmitter<CdkDragDrop<TileId[]>> = new EventEmitter<CdkDragDrop<TileId[]>>();
  @Output() sortedById$: EventEmitter<TileId[]> = new EventEmitter<TileId[]>();
  @Output() hiddenDataPointsUpdate$: EventEmitter<DataPoint[]> = new EventEmitter<DataPoint[]>();


  constructor() {
  }


  drop($event: CdkDragDrop<TileId[]>) {
    if ($event.previousContainer === $event.container) {
      moveItemInArray(
        $event.container.data,
        $event.previousIndex,
        $event.currentIndex,
      );
    } else {
      transferArrayItem(
        $event.previousContainer.data,
        $event.container.data,
        $event.previousIndex,
        $event.currentIndex,
      );
    }

    this.draggedAndDropped$.emit($event);
  }


  get activeSortDirection(): SortTableSortDirection {
    if (!this.sortDirection || !this.tileOrder) {
      return undefined;
    }

    const sorted = this.sortedCopy(this.sortDirection);

    return sorted.every((tileId, index) => tileId === this.tileOrder[index])
      ? this.sortDirection
      : undefined;
  }


  /**
   * Cycles Tile ID sorting like a table column header: up, down, then back
   * to the manual order the list had before sorting started.
   */
  sortById() {
    const current = this.activeSortDirection;

    if (current === undefined) {
      this.manualOrder = [...this.tileOrder];
      this.sortDirection = 'up';
      this.sortedById$.emit(this.sortedCopy('up'));
    } else if (current === 'up') {
      this.sortDirection = 'down';
      this.sortedById$.emit(this.sortedCopy('down'));
    } else {
      this.sortDirection = undefined;
      this.sortedById$.emit(this.restoredManualOrder());
    }
  }


  private sortedCopy(direction: SortTableSortDirection): TileId[] {
    const factor = direction === 'up' ? 1 : -1;

    return [...this.tileOrder].sort((a, b) => {
      const aExists = this.tileExists(a);
      const bExists = this.tileExists(b);

      if (aExists !== bExists) {
        return aExists ? -1 : 1;
      }

      return factor * a.localeCompare(b, undefined, {numeric: true});
    });
  }


  private restoredManualOrder(): TileId[] {
    const present = new Set(this.tileOrder);
    const known = new Set(this.manualOrder);

    return [
      ...this.manualOrder.filter(tileId => present.has(tileId)),
      ...this.tileOrder.filter(tileId => !known.has(tileId)),
    ];
  }


  openTilesMenu() {
    this.menuOpen = true;
  }


  closeTilesMenu() {
    this.menuOpen = false;
  }


  onClickX(tileId: TileId) {
    this.clickedX$.emit(tileId);
  }


  onClickOption(option: Option) {
    this.clickedOption$.emit(option);
  }

  hideTile(tileId: TileId) {

    const isDataPointsEmpty = !this.dataPoints?.length;
    if (isDataPointsEmpty) {
      this.dataPoints = this.defaultDataPointsArray;
    }

    let dataPoint = this.dataPoints.find(dataPoint => dataPoint.TileID === tileId);
    if (!dataPoint) {
      dataPoint = { TileID: tileId, Hidden: false };
      this.dataPoints = [...this.dataPoints, dataPoint];
    }
    dataPoint.Hidden = true;

    this.hiddenDataPointsUpdate$.emit(this.dataPoints);
  }

  get defaultDataPointsArray(): DataPoint[] {
    return [...this.tileOrder].map(tileId => ({ TileID: tileId, Hidden: false}));
  }

  showTile(tileId: TileId) {
    const dataPoint = this.dataPoints.find(dataPoint => dataPoint.TileID === tileId);
    dataPoint.Hidden = false;

    this.hiddenDataPointsUpdate$.emit(this.dataPoints);
  }

  isHiddenTile(tileId: TileId): boolean {
    return this.dataPoints?.find(dataPoint => dataPoint.TileID === tileId)?.Hidden;
  }

  /**
   * Check if tile exists (not deleted)
   * Tiles that don't exist in the tiles map are considered deleted
   */
  tileExists(tileId: TileId): boolean {
    return !!this.tiles?.[tileId];
  }

  getFacetText(tileId: TileId): string {
    return this.tiles[tileId]?.facets.map(f => f.Value).join(' · ') ?? '';
  }
}
